import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useMemo, useState, } from 'react';
import { useCamadas } from '@/map/camadas';
const ContextoDaExploracao = createContext(null);
/**
 * Provider do contexto de exploração.
 *
 * Centraliza a máquina de estados da navegação contextual e expõe transições
 * coordenadas. A seleção geográfica da feição é independente do nível de
 * navegação: nenhuma transição deste provider a limpa.
 */
export function ExploracaoProvider({ children }) {
    const { definirSelecaoDaCamada } = useCamadas();
    const [nivel, setNivel] = useState('catalogo');
    const [camadaId, setCamadaId] = useState(null);
    const [recorteTemporal, setRecorteTemporal] = useState('atual');
    const [identidade] = useState(null);
    // Estrutura preparada para sincronização futura entre catálogo e exploração.
    // Mantida como vetor vazio até que o contrato de visibilidade exigido
    // amadureça; evita-se estado paralelo a `useCamadas.ativas`.
    const camadasAtivas = [];
    const alternarAtivacaoDaCamada = useCallback(() => {
        // Placeholder estrutural: a ativação real segue em `useCamadas.alternarAtiva`.
        // Esta API fica reservada para sincronização com níveis temporais futuros.
    }, []);
    const irParaExploracao = useCallback((id) => {
        setNivel('exploracao');
        setCamadaId(id);
    }, []);
    const irParaInspecao = useCallback((id) => {
        setNivel('inspecao');
        setCamadaId(id);
    }, []);
    /**
     * Fecha a inspeção voltando ao nível de exploração da camada.
     *
     * **Importante:** não chama `useSelecao.limpar()`. A inspeção aberta
     * (`nivel === 'inspecao'`) e a feição destacada (`selecionada`) são
     * estados distintos: ao fechar o inspetor, o destaque e o enquadramento
     * da feição permanecem ativos no mapa. Nenhuma transição de navegação
     * limpa a seleção geográfica.
     */
    const voltarDeInspecao = useCallback(() => {
        setNivel((atual) => (atual === 'inspecao' ? 'exploracao' : atual));
    }, []);
    /**
     * Encerramento coordenado da exploração de uma camada.
     *
     * Operação única compartilhada por `voltarDaExploracao` e `irParaCatalogo`,
     * eliminando o caminho inconsistente em que `irParaCatalogo` apenas
     * ressetava nível e `camadaId` sem restaurar o filtro MVT.
     *
     * A ordem importa:
     *   1. Restaura visibilidade integral da camada explorada (zerando
     *      somente as marcações individuais no filtro MVT).
     *   2. Transiciona o nível para `catalogo`.
     *   3. Zera o `camadaId` em contexto.
     *
     * A restauração do filtro MVT não é limpeza de feição: a seleção
     * geográfica (`useSelecao`) e seu destaque no mapa são preservados, assim
     * como a seleção de camada do `CamadasProvider`. Não desativa a camada
     * (`ativas` não é tocado), não modifica outras camadas e não move a
     * câmera. As demais camadas publicadas permanecem com o mesmo estado de
     * visibilidade.
     */
    const encerrarExploracao = useCallback(() => {
        if (camadaId !== null) {
            definirSelecaoDaCamada(camadaId, { todos: true, ids: [] });
        }
        setNivel('catalogo');
        setCamadaId(null);
    }, [camadaId, definirSelecaoDaCamada]);
    const voltarDaExploracao = useCallback(() => {
        encerrarExploracao();
    }, [encerrarExploracao]);
    /**
     * `irParaCatalogo` delega à operação única de encerramento. A API
     * pública do contexto permanece compatível com seus consumidores
     * (assinatura e semântica) e o caminho duplicado/inconsistente deixa
     * de existir: chegar ao catálogo pelo atalho explícito ou pelo
     * retorno da exploração executa exatamente o mesmo conjunto de
     * ações, na mesma ordem.
     */
    const irParaCatalogo = useCallback(() => {
        encerrarExploracao();
    }, [encerrarExploracao]);
    const definirRecorteTemporal = useCallback((m) => {
        setRecorteTemporal(m);
    }, []);
    const valor = useMemo(() => ({
        nivel,
        camadaId,
        recorteTemporal,
        identidade,
        irParaCatalogo,
        irParaExploracao,
        irParaInspecao,
        voltarDeInspecao,
        voltarDaExploracao,
        definirRecorteTemporal,
        alternarAtivacaoDaCamada,
        camadasAtivas,
    }), 
    // `camadasAtivas` é derivado estável (vetor vazio); não participa do cache.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        nivel,
        camadaId,
        recorteTemporal,
        identidade,
        irParaCatalogo,
        irParaExploracao,
        irParaInspecao,
        voltarDeInspecao,
        voltarDaExploracao,
        definirRecorteTemporal,
        alternarAtivacaoDaCamada,
    ]);
    return (_jsx(ContextoDaExploracao.Provider, { value: valor, children: children }));
}
/** Lê o contexto de exploração. Lança erro se não houver Provider acima. */
export function useExploracao() {
    const contexto = useContext(ContextoDaExploracao);
    if (contexto === null) {
        throw new Error('useExploracao exige ExploracaoProvider acima na árvore');
    }
    return contexto;
}
